<script>
import { mapActions, mapState } from "vuex";

import InfoTooltip from "@/components/InfoTooltip";
import CardTableLinkIndicator from "@/components/CardTableLinkIndicator";
import { formatHashrate, formatRelativeDate } from "@/utils";

export default {
  // Includede components
  components: {
    InfoTooltip,
    CardTableLinkIndicator,
  },

  props: {
    // The group ID whose workers should be shown -- if not set,
    // will show the workers for the whole team
    groupId: {
      default: null,
    },

    // whether or not to show inactive workers for the group/team
    inactive: {
      default: false,
    },
  },

  data() {
    return {
      // list of workers
      currentWorkers: null,

      // Whether to show new workers (vs just ones that have submitted)
      showNewWorkers: false,

      // The current request limit
      limit: 25,

      // The current request offset
      offset: 0,

      // Whether there is another page of data
      hasNextPage: false,

      // Whether the current result is using the ?submitted filter
      resultIsSubmitted: false,
    };
  },

  // When the component is created, fetch the inactive workers
  async created() {
    this.fetchWorkers();
  },

  computed: {
    // Mapped state
    ...mapState(["workers", "workerShares"]),
  },

  methods: {
    // The initial load of this component will only load the first 25 workers.
    // The next showMore event will append the next 45 (up to a total of 50).
    // Every subsequent call will append the next 50.
    async showMore() {
      this.offset += 25;
      this.fetchWorkers();
    },

    // Fetch workers from the API
    async fetchWorkers(replace = false) {
      let result = await this.getInactiveWorkers({
        groupId: this.groupId,
        limit: this.limit,
        submitted: this.inactive ? !this.showNewWorkers : false,
        offset: this.offset,
        inactive: this.inactive,
      });

      // Either append or replace the current set of workers
      if (this.currentWorkers !== null && !replace) {
        this.currentWorkers = this.currentWorkers.concat(result.workers);
      } else {
        this.currentWorkers = result.workers;
      }

      if (!this.inactive) {
        // only fetch worker data if all workers is being listed
        await this.getWorkerData(result.workers);
      }

      // Reset internal metdata about the result
      this.hasNextPage = result.paging.next_page !== null;
      this.resultIsSubmitted = this.inactive ? !this.showNewWorkers : false;
    },

    // Refetch data for each worker
    async getWorkerData(newWorkers) {
      // Get share data for each worker id
      await Promise.all(
        newWorkers.map((worker) => {
          Promise.all([this.getWorkerShares({ workerId: worker.id })]);
        })
      );
    },

    // Redirect to a selected worker
    goToWorker(worker) {
      this.$router.push(`/workers/${worker.id}`);
    },

    // Helper methods and mapped actions
    formatHashrate,
    formatRelativeDate,
    ...mapActions([
      "getInactiveWorkers",
      "getWorkerShares",
      "getWorkerHashrate",
      "getWorkerLastSubmission",
    ]),
  },

  filters: {
    truncate(label) {
      if (label.length >= 25) {
        return (
          label.substr(0, 10) + "..." + label.substr(label.length - 10, 10)
        );
      } else {
        return label;
      }
    },
  },
};
</script>

<template>
  <div v-if="!this.inactive" class="Group-workers">
    <b-row class="mb-3">
      <b-col>
        <h3>All Workers</h3>
      </b-col>
    </b-row>
    <b-row>
      <b-col v-if="!this.currentWorkers || this.currentWorkers.length === 0">
        <b-card class="text-center py-5" key="no-workers">
          <b-card-title>No Workers</b-card-title>
          <b-card-text>
            Workers represent physical or virtual miners
          </b-card-text>
          <b-button class="float-center" variant="primary" to="/workers/create">
            Add Worker
          </b-button>
        </b-card>
      </b-col>
      <b-col v-else>
        <b-table-simple striped hover responsive>
          <b-thead>
            <b-tr>
              <b-th>Name</b-th>
              <b-th>Pool Worker Name</b-th>
              <b-th>Device</b-th>
              <b-th>Hashrate</b-th>
              <b-th>Last Submission</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr v-for="(worker, index) in this.currentWorkers" :key="index">
              <b-td>
                <b-link :to="`/workers/${worker.id}`">
                  {{ worker.name }}
                </b-link>
              </b-td>
              <div v-if="worker.pool_configuration">
                <b-td v-if="worker.pool_configuration.worker_name.length >= 20">
                  <div
                    v-b-tooltip.hover="worker.pool_configuration.worker_name"
                  >
                    {{
                      worker.pool_configuration
                        ? worker.pool_configuration.worker_name
                        : "" | truncate
                    }}
                  </div>
                </b-td>
                <b-td v-if="worker.pool_configuration.worker_name.length < 20">
                  <div>
                    {{
                      worker.pool_configuration
                        ? worker.pool_configuration.worker_name
                        : "" | truncate
                    }}
                  </div>
                </b-td>
              </div>
              <div v-if="!worker.pool_configuration">
                <b-td>None</b-td>
              </div>

              <b-td>{{ worker.device.name }}</b-td>
              <b-td>{{ formatHashrate(worker.hashrate) }}</b-td>
              <b-td>
                {{ formatRelativeDate(worker.last_submitted) }}
              </b-td>
            </b-tr>
          </b-tbody>
          <b-tfoot v-if="this.hasNextPage">
            <tr id="dt-show-more">
              <b-td
                colspan="4"
                variant="light"
                class="text-center"
                @click="showMore"
              >
                <b>Show More Workers</b>
              </b-td>
            </tr>
          </b-tfoot>
        </b-table-simple>
      </b-col>
    </b-row>
  </div>

  <div v-else class="Inactive-workers">
    <b-row class="mb-3">
      <b-col>
        <h3>Inactive Workers</h3>
      </b-col>
      <b-col md="auto" align-self="center">
        <b-form-checkbox v-model="showNewWorkers">
          Show New Workers
          <InfoTooltip
            text="New workers are those that have never submitted."
          />
        </b-form-checkbox>
      </b-col>
    </b-row>
    <b-card
      class="text-center py-5"
      v-if="workers.length === 0 && currentWorkers !== null"
      key="no-workers"
    >
      <b-card-title>No Workers</b-card-title>
      <b-card-text>Workers represent physical or virtual miners</b-card-text>
      <b-button class="float-center" variant="primary" to="/workers/create">
        Add Worker
      </b-button>
    </b-card>
    <b-card
      class="text-center py-5"
      v-else-if="currentWorkers !== null && currentWorkers.length === 0"
      key="no-inactive-workers"
    >
      <b-card-title>No Inactive Workers</b-card-title>
      <b-card-text v-if="resultIsSubmitted">
        All workers have submitted in the last 5 minutes or have never submitted
      </b-card-text>
      <b-card-text v-else>
        All workers have submitted in the last 5 minutes
      </b-card-text>
    </b-card>
    <b-card
      class="Card-table Card-table-hoverable"
      v-else-if="currentWorkers !== null"
      key="some"
    >
      <b-table
        :items="currentWorkers"
        :fields="[
          { key: 'name', label: 'Name' },
          { key: 'pool_worker_name', label: 'Pool Worker Name' },
          { key: 'last_submitted', label: 'Last submitted' },
        ]"
        @row-clicked="goToWorker"
      >
        <template #cell(name)="data">
          <b-link :to="`/workers/${data.item.id}`">{{ data.item.name }}</b-link>
          <CardTableLinkIndicator />
        </template>
        <template #cell(pool_worker_name)="data">
          <div v-if="data.item.pool_configuration">
            <div v-if="data.item.pool_configuration.worker_name.length >= 25">
              <div v-b-tooltip.hover="data.item.pool_configuration.worker_name">
                {{
                  data.item.pool_configuration
                    ? data.item.pool_configuration.worker_name
                    : "N/A" | truncate
                }}
              </div>
            </div>
            <div v-if="data.item.pool_configuration.worker_name.length < 25">
              {{
                data.item.pool_configuration
                  ? data.item.pool_configuration.worker_name
                  : "N/A" | truncate
              }}
            </div>
          </div>
          <div v-if="!data.item.pool_configuration">None</div>
        </template>
        <template #cell(last_submitted)="data">
          {{ formatRelativeDate(data.item.last_submitted) }}
        </template>
      </b-table>
      <b-card-footer v-if="hasNextPage" @click="showMore">
        <center>
          <a>Show More</a>
        </center>
      </b-card-footer>
    </b-card>
  </div>
</template>

<style scoped>
#dt-show-more:hover {
  cursor: pointer;
}
</style>
